import { can, type Capability } from '../../lib/api'
import { onScreen } from '../../lib/screens'
import './assess.css'

// The lists under Assessments, as one row of tabs at the top of each (release
// 3.3, O1 of the 7 Oct plan, section 3.6).
//
// # WHY THIS EXISTS
//
// Assessments, the bulk sends, findings, risk acceptances and reassessment are
// five lists of the same kind under one menu section, and each was a page of
// its own that named none of the others: a person on Findings had to go back
// to the menu to see the risk acceptances. The row says where the others are
// and which one this is.
//
// # WHAT IT IS AND IS NOT
//
// Links, not tabs in the ARIA sense: each list keeps its own address
// (#/campaigns and the rest still open the same page, from a menu, a link in
// an email or Back), so moving between them is moving between pages, marked
// with aria-current. Questionnaires is not in the row: it is the library the
// lists send from, not a list of work.
//
// A reader is offered only the lists the menu offers them: their roles open
// the page (the capability each page needs, App.tsx SCREEN_NEEDS) AND it is
// part of the signed-in persona's job (lib/screens.ts), as App.tsx mayOpen
// asks; a tab to another job's list would open the "not your job" page.
// test/assessments-o1.test.mjs holds the tables together. With one list left there is nothing to move between, so
// no row is drawn.
//
// The words are the glossary's (7 Oct plan, "Campaigns" and "Accepted risks"
// become "Bulk assessments" and "Risk acceptances"). Where a page's own title
// still says the old word, it changes with that page.

export type AssessScreen = 'assessments' | 'campaigns' | 'findings' | 'exceptions' | 'sustain'

export const ASSESS_TABS: { screen: AssessScreen; label: string; href: string; needs: Capability }[] = [
  { screen: 'assessments', label: 'Assessments', href: '#/assessments', needs: 'assessment.read' },
  { screen: 'campaigns', label: 'Bulk assessments', href: '#/campaigns', needs: 'assessment.read' },
  { screen: 'findings', label: 'Findings', href: '#/findings', needs: 'assessment.read' },
  { screen: 'exceptions', label: 'Risk acceptances', href: '#/exceptions', needs: 'session.read' },
  { screen: 'sustain', label: 'Reassessment', href: '#/sustain', needs: 'assessment.read' },
]

// The tabs a reader is offered, in order.
export function assessTabs(may: (c: Capability) => boolean, shown: (screen: string) => boolean = () => true): typeof ASSESS_TABS {
  return ASSESS_TABS.filter((t) => may(t.needs) && shown(t.screen))
}

export function AssessTabsView({ at, tabs }: { at: AssessScreen; tabs: typeof ASSESS_TABS }) {
  if (tabs.length < 2) return null
  return (
    <nav className="as-tabs" aria-label="Assessment lists">
      {tabs.map((t) => (
        <a key={t.screen} href={t.href} className={'as-tab' + (t.screen === at ? ' on' : '')} aria-current={t.screen === at ? 'page' : undefined}>
          {t.label}
        </a>
      ))}
    </nav>
  )
}

export function AssessTabs({ at }: { at: AssessScreen }) {
  return <AssessTabsView at={at} tabs={assessTabs(can, onScreen)} />
}
